import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Card from "@mui/material/Card";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import ErrorAlert from "components/ErrorAlert";
import Pagination from "utils/Pagination";
import GoalButton from "layouts/goals/components/GoalButton";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import useAssets from "./useAssets";
import AssetsTable from "./components/AssetsTable";
import AssetFormModal from "./components/AssetFormModal";
import AssetDetailsModal from "./components/AssetDetailsModal";
import AssignAssetModal from "./components/AssignAssetModal";
import ReturnAssetModal from "./components/ReturnAssetModal";
import DisposeAssetModal from "./components/DisposeAssetModal";
import DeleteAssetModal from "./components/DeleteAssetModal";
import { ASSET_STATES, ASSET_TYPES, searchableText } from "./assets.constants";

const PAGE_SIZE = 10;

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All types" },
  ...ASSET_TYPES.map(({ value, label }) => ({ value, label })),
];
const STATE_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  ...ASSET_STATES.map(({ value, label }) => ({ value, label })),
];

function Stat({ label, value }) {
  return (
    <Card sx={{ flex: "1 1 160px" }}>
      <SuiBox p={2}>
        <SuiTypography variant="caption" color="secondary" display="block">
          {label}
        </SuiTypography>
        <SuiTypography variant="h4" fontWeight="bold">
          {value}
        </SuiTypography>
      </SuiBox>
    </Card>
  );
}

function Assets() {
  const {
    auth: { admin },
  } = useSelector((state) => state);

  const {
    assets,
    assignees,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createAsset,
    updateAsset,
    deleteAsset,
    assignAsset,
    returnAsset,
    disposeAsset,
    clearError,
    clearSuccess,
  } = useAssets(admin?.id);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Which dialog is open, and for which asset.
  const [formTarget, setFormTarget] = useState(null); // { asset } ; asset null = new
  const [viewing, setViewing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [returning, setReturning] = useState(null);
  const [disposing, setDisposing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchAll();
  }, [admin?.id]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return assets.filter((asset) => {
      if (typeFilter && asset.type !== typeFilter) return false;
      if (stateFilter && asset.state !== stateFilter) return false;
      return !term || searchableText(asset).includes(term);
    });
  }, [assets, search, typeFilter, stateFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter, stateFilter]);

  const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const counts = useMemo(() => {
    const company = assets.filter((a) => a.status === "active" || a.status === "in_repair");
    const countState = (state) => assets.filter((a) => a.state === state).length;
    return {
      company: company.length,
      assigned: countState("assigned"),
      inStock: countState("in_stock"),
      inRepair: countState("in_repair"),
    };
  }, [assets]);

  const spareAssets = useMemo(() => assets.filter((a) => a.state === "in_stock"), [assets]);

  // Runs a request with the button spinner, and closes the dialog on success.
  const submit =
    (action, close) =>
    async (...args) => {
      setIsSubmitting(true);
      const ok = await action(...args);
      setIsSubmitting(false);
      if (ok) close();
    };

  const handleSave = submit(
    (body) => (formTarget.asset ? updateAsset(formTarget.asset.id, body) : createAsset(body)),
    () => setFormTarget(null)
  );
  const handleAssign = submit(assignAsset, () => setAssigning(null));
  const handleReturn = submit(returnAsset, () => setReturning(null));
  const handleDispose = submit(disposeAsset, () => setDisposing(null));
  const handleDelete = submit(
    () => deleteAsset(deleting.id),
    () => setDeleting(null)
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox py={3}>
        {errorMessage && (
          <ErrorAlert color="error" message={errorMessage} clearError={clearError} />
        )}
        {successMessage && (
          <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
        )}
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <SuiTypography variant="h5" fontWeight="medium">
            Assets
          </SuiTypography>
          <SuiBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <SuiBox minWidth="240px">
              <SuiInput
                placeholder="Search name, serial, IMEI, person..."
                icon={{ component: "search", direction: "left" }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SuiBox>
            <SuiBox minWidth="150px">
              <CustomSelect
                options={TYPE_FILTER_OPTIONS}
                value={TYPE_FILTER_OPTIONS.find((option) => option.value === typeFilter)}
                onChange={(option) => setTypeFilter(option.value)}
                customStyles={goalSelectStyles}
                menuPortalTarget={document.body}
              />
            </SuiBox>
            <SuiBox minWidth="160px">
              <CustomSelect
                options={STATE_FILTER_OPTIONS}
                value={STATE_FILTER_OPTIONS.find((option) => option.value === stateFilter)}
                onChange={(option) => setStateFilter(option.value)}
                customStyles={goalSelectStyles}
                menuPortalTarget={document.body}
              />
            </SuiBox>
            <GoalButton variant="primary" icon="add" onClick={() => setFormTarget({ asset: null })}>
              Add asset
            </GoalButton>
          </SuiBox>
        </SuiBox>

        <SuiBox display="flex" gap={2} flexWrap="wrap" mb={3}>
          <Stat label="Total assets" value={counts.company} />
          <Stat label="Assigned" value={counts.assigned} />
          <Stat label="In stock (spare)" value={counts.inStock} />
          <Stat label="In repair" value={counts.inRepair} />
        </SuiBox>

        <AssetsTable
          assets={pageRows}
          isLoading={isLoading}
          onView={setViewing}
          onEdit={(asset) => setFormTarget({ asset })}
          onAssign={setAssigning}
          onReturn={setReturning}
          onDispose={setDisposing}
          onDelete={setDeleting}
        />
        {!isLoading && (
          <SuiBox mt={3}>
            <Pagination
              currentPage={currentPage}
              totalCount={visible.length}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </SuiBox>
        )}

        <AssetFormModal
          open={!!formTarget}
          onClose={() => setFormTarget(null)}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          asset={formTarget?.asset || null}
        />
        <AssetDetailsModal open={!!viewing} onClose={() => setViewing(null)} asset={viewing} />
        <AssignAssetModal
          open={!!assigning}
          onClose={() => setAssigning(null)}
          onAssign={handleAssign}
          isSubmitting={isSubmitting}
          asset={assigning}
          employee={null}
          spareAssets={spareAssets}
          assignees={assignees}
        />
        <ReturnAssetModal
          open={!!returning}
          onClose={() => setReturning(null)}
          onReturn={handleReturn}
          isSubmitting={isSubmitting}
          asset={returning}
        />
        <DisposeAssetModal
          open={!!disposing}
          onClose={() => setDisposing(null)}
          onDispose={handleDispose}
          isSubmitting={isSubmitting}
          asset={disposing}
        />
        <DeleteAssetModal
          open={!!deleting}
          onClose={() => setDeleting(null)}
          onConfirm={handleDelete}
          assetName={deleting?.name}
          isSubmitting={isSubmitting}
        />
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Assets;
